import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import {
  SITE_ORIGIN,
  BLOG_SLUG_FREE_WEBSITE_UK,
  blogArticleUrl,
  internalUrl
} from './siteConfig';

const CANONICAL = blogArticleUrl(BLOG_SLUG_FREE_WEBSITE_UK);
const ARTICLE_SCHEMA_ID = 'webcodecraft-article-free-websites-uk';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline:
    'Free websites in the UK: DIY builders vs custom development — and what “free” really means',
  description:
    'Honest answers to common questions about free websites in the UK, Wix-style builders vs custom sites, and how WebCodeCraft fits in—from the South West to clients nationwide.',
  datePublished: '2026-03-21',
  dateModified: '2026-03-21',
  author: {
    '@type': 'Organization',
    name: 'WebCodeCraft',
    url: SITE_ORIGIN
  },
  publisher: {
    '@type': 'Organization',
    name: 'WebCodeCraft',
    url: SITE_ORIGIN
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': CANONICAL
  }
};

function BlogArticleFreeWebsiteUk() {
  useEffect(() => {
    document.title =
      'Free websites in the UK vs Wix & builders | WebCodeCraft';

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', CANONICAL);
    }

    const existing = document.getElementById(ARTICLE_SCHEMA_ID);
    if (!existing) {
      const el = document.createElement('script');
      el.id = ARTICLE_SCHEMA_ID;
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(articleJsonLd);
      document.head.appendChild(el);
    }

    return () => {
      document.title = 'WebCodeCraft | Free Website Design & Build UK';
      if (canonical) {
        canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      }
      const script = document.getElementById(ARTICLE_SCHEMA_ID);
      if (script) script.remove();
    };
  }, []);

  return (
    <>
      <Header />
      <main id="main-content" className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <article className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <Link to="/guides" className="text-gray-600 hover:text-indigo-600 hover:underline">
                Guides
              </Link>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Free websites in the UK: DIY builders vs custom development — and what &quot;free&quot;
              really means
            </h1>

            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              If you&apos;ve seen searches like &quot;how to set up a free website in the UK&quot; or
              &quot;WebCodeCraft vs Wix&quot;, this guide gives straight answers — including where drag-and-drop
              tools shine, where a small South West agency fits, and how our own model works.
            </p>

            <div className="space-y-10 text-gray-700 leading-relaxed">
              <section aria-labelledby="q-100-free">
                <h2 id="q-100-free" className="text-2xl font-bold text-gray-900 mb-4">
                  Is it possible to make a 100% free website?
                </h2>
                <p className="mb-4">
                  You can publish something on the internet at <span className="font-semibold text-gray-900">no upfront cost</span> using
                  free tiers from website builders, blogging platforms, or simple hosting offers. In that narrow sense,
                  &quot;100% free&quot; is possible for a basic presence — often with a branded subdomain (e.g.{' '}
                  <span className="whitespace-nowrap">yourname.wixsite.com</span>), limited storage, and builder branding.
                </p>
                <p className="mb-4">
                  What is rarely &quot;free&quot; in practice is everything businesses care about long term: a custom domain,
                  ad-free pages, enough speed and storage, proper email deliverability from forms, ongoing security
                  updates, and someone to call when something breaks. Those either appear as{' '}
                  <span className="font-semibold text-gray-900">paid upgrades</span> or your own time spent learning and fixing issues.
                </p>
                <p>
                  So: <span className="font-semibold text-gray-900">yes</span>, you can start for £0;
                  staying professional, fast, and maintainable usually involves cost — money, time, or both.
                </p>
              </section>

              <section aria-labelledby="q-full-free">
                <h2 id="q-full-free" className="text-2xl font-bold text-gray-900 mb-4">
                  How can I create a full website for free?
                </h2>
                <p className="mb-4">
                  A &quot;full&quot; website means different things: a one-page leaflet, a booking site, a shop with
                  payments, or a multi-language brochure. Free builder plans typically cover{' '}
                  <span className="font-semibold text-gray-900">simple sites</span> well; they get harder when you need
                  custom checkout flows, memberships, complex SEO, or integrations with tools you already use (CRM,
                  accounting, booking APIs).
                </p>
                <p className="mb-4">
                  Common path: pick a template, customise content, connect a domain (usually paid), and add plugins or
                  higher tiers as you grow. Many UK small businesses outgrow the cheapest tier within a year — which is
                  fine if you planned for it.
                </p>
                <p>
                  If you need a <span className="font-semibold text-gray-900">bespoke layout, performance budget, or brand-specific UX</span>,
                  builders can hit limits; that&apos;s where custom development (or a hybrid) enters the conversation.
                </p>
              </section>

              <section aria-labelledby="q-uk-setup">
                <h2 id="q-uk-setup" className="text-2xl font-bold text-gray-900 mb-4">
                  How to set up a free website in the UK
                </h2>
                <p className="mb-4">
                  Practically: choose a platform (self-hosted WordPress, an all-in-one builder, or a developer-led
                  stack), register a domain if you want a professional <span className="font-semibold text-gray-900">.co.uk</span> or{' '}
                  <span className="font-semibold text-gray-900">.uk</span> address, publish core pages (Home, Services, Contact, Privacy),
                  connect SSL (often included), and submit a sitemap in{' '}
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 font-medium hover:underline"
                  >
                    Google Search Console
                  </a>
                  . UK-specific concerns are usually <span className="font-semibold text-gray-900">GDPR-aware forms</span>, clear company
                  contact details, and hosting location if that matters for your clients — not a different &quot;free&quot; stack
                  per se.
                </p>
                <p>
                  Our own clients often combine <span className="font-semibold text-gray-900">clear UK-facing messaging</span> with hosting
                  and support that doesn&apos;t fall on them alone.
                </p>
              </section>

              <section aria-labelledby="builders-vs-custom">
                <h2 id="builders-vs-custom" className="text-2xl font-bold text-gray-900 mb-4">
                  Website builders (Wix, Squarespace, etc.) vs custom development
                </h2>
                <p className="mb-4">
                  <span className="font-semibold text-gray-900">Drag-and-drop builders</span> are excellent for getting online quickly,
                  trying ideas, and managing content in-house with visual editors. Trade-offs can include template
                  constraints, plugin costs, performance tuning limits, and migration effort if you later need a fully
                  custom stack.
                </p>
                <p className="mb-4">
                  <span className="font-semibold text-gray-900">Custom development</span> (e.g. React, headless CMS, or tailored backends)
                  fits when you need a specific user journey, integrations, or performance characteristics that templates
                  struggle to match. It usually costs more upfront — unless you work with a team that spreads cost
                  differently (see below).
                </p>
                <p>
                  WebCodeCraft isn&apos;t here to claim &quot;builders are bad&quot;; we care about{' '}
                  <span className="font-semibold text-gray-900">fit for your goals</span>. Some businesses stay on builders forever; others
                  graduate to custom when revenue or complexity justifies it.
                </p>
              </section>

              <section aria-labelledby="webcodecraft-model">
                <h2 id="webcodecraft-model" className="text-2xl font-bold text-gray-900 mb-4">
                  How WebCodeCraft compares (without the hype)
                </h2>
                <p className="mb-4">
                  We focus on <span className="font-semibold text-gray-900">custom sites and real support</span>, with a model designed to
                  reduce large upfront fees: we design and build your site, and you pay an agreed{' '}
                  <span className="font-semibold text-gray-900">monthly hosting and service</span> fee so the site stays live, secured,
                  backed up, and maintainable — details match the package you choose.
                </p>
                <p className="mb-4">
                  That is <span className="font-semibold text-gray-900">not</span> the same as &quot;£0 forever with no strings&quot;; it
                  <span className="font-semibold text-gray-900"> is</span> transparent about where value goes: professional delivery
                  instead of a giant invoice before launch.
                </p>
                <p>
                  If you&apos;re comparing <span className="font-semibold text-gray-900">WebCodeCraft vs Wix</span> (or similar), the
                  question isn&apos;t only monthly price — it&apos;s <span className="font-semibold text-gray-900">who owns the roadmap</span>,
                  how bespoke the experience is, and what happens when you need changes six months after launch.
                </p>
              </section>

              <section aria-labelledby="which-right">
                <h2 id="which-right" className="text-2xl font-bold text-gray-900 mb-4">
                  Which option is right for you?
                </h2>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <span className="font-semibold text-gray-900">Builder:</span> fastest DIY path, you&apos;re happy to
                    maintain content yourself, and limits are acceptable.
                  </li>
                  <li>
                    <span className="font-semibold text-gray-900">Agency / custom:</span> you want guided delivery,
                    clearer accountability, and room to grow without fighting the template.
                  </li>
                </ul>
                <p className="mt-6">
                  Next step: read our{' '}
                  <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
                    FAQ
                  </a>
                  , then{' '}
                  <a href={internalUrl('contact')} className="text-indigo-600 font-medium hover:underline">
                    tell us about your project
                  </a>
                  — we&apos;ll be honest about whether we&apos;re a good match.
                </p>
              </section>
            </div>

            <div className="mt-14 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 text-white">
              <h3 className="text-xl font-bold mb-2">Ready to talk?</h3>
              <p className="mb-4 opacity-95">
                No obligation — share your goals and we&apos;ll outline realistic options.
              </p>
              <a
                href={internalUrl('contact')}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Contact WebCodeCraft
              </a>
            </div>
          </m.div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default BlogArticleFreeWebsiteUk;
